import React, {
  useCallback, useState, useEffect, memo,
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import { clearAutocomplete, hotelAutocompleteRequest } from '../../../../../store/slices/autocomplete';
import { hotelSearch } from '../../../../../store/slices/hotel';
import { setHotelSearchObjFromQueryString, setHotelTripSearchInputs } from '../../../../../store/slices/tripSearch';
import {
  SearchStatus, checkDoSearchDeepLinkWithoutDate, PageLinks,
  getAutocompleteType,
} from '../../../../../constants';
import SearchAutocomplete from '../../../../SearchAutocomplete';
import hotelSearchInput from '../../../../../helpers/selectors/hotelSearchInput';

const HotelsSearch = ({ tripId, defaultSearchLocationName, doUseThemeBackground }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { track } = useAnalytics();
  const isHotelTripSearch = !tripId;

  const hotelsAutocompleteResults = useSelector(
    (store) => store.autocomplete.HotelsAutocompleteResults,
  );

  const { hotelAutocompleteRequestStatus } = useSelector((store) => store.autocomplete);

  const inputs = useSelector(hotelSearchInput(tripId, isHotelTripSearch));
  const { travelAgency } = useSelector((store) => store.user);
  const hotelAddressInputPlaceholder = travelAgency && travelAgency.filterSettings
    && travelAgency.filterSettings.hotelAddressInputPlaceholder;
  let { selectedAddress } = inputs;
  const [autocompleteValue, setAutocompleteValue] = useState();
  const [isUserSearch, setIsUserSearch] = useState(false);
  const [isDirtySearch, setIsDirtySearch] = useState(false);

  const doAllowDeepLinkWithoutDate = travelAgency && travelAgency.settings
    && travelAgency.settings.doAllowDeepLinkWithoutDate;
  if (isHotelTripSearch) { selectedAddress = inputs.searchInputs.selectedAddress; }
  const {
    travelOptions,
  } = useSelector((state) => state.tripSearch.searchResult);

  const isDedicatedHotelPage = router.route === PageLinks.DedicatedHotelPage.route;

  useEffect(() => {
    if (isUserSearch) return;
    if (((isDedicatedHotelPage && _.isNil(selectedAddress))
       || checkDoSearchDeepLinkWithoutDate(router.query, doAllowDeepLinkWithoutDate))
      && hotelsAutocompleteResults
      && travelOptions && travelOptions[0]
      && travelOptions[0].hotelItinerary
      && travelOptions[0].hotelItinerary.hotel) {
      const query = `${travelOptions[0].hotelItinerary.hotel.providerId}`;
      const filteredSuggests = hotelsAutocompleteResults
        .map((hotel) => hotel.suggests.find((suggest) => suggest.hotelId === query))
        .filter((suggest) => suggest !== undefined);
      if (filteredSuggests) {
        dispatch(setHotelTripSearchInputs({ selectedAddress: filteredSuggests[0], page: 1 }));
        dispatch(setHotelSearchObjFromQueryString({ selectedAddress: filteredSuggests[0] }));
      }
    }
  }, [router.query, isDedicatedHotelPage, doAllowDeepLinkWithoutDate,
    hotelsAutocompleteResults, travelOptions, dispatch, isUserSearch, selectedAddress]);

  useEffect(() => {
    if (selectedAddress) {
      setAutocompleteValue(selectedAddress);
      return;
    }
    if (!isDirtySearch) {
      setAutocompleteValue({
        label: defaultSearchLocationName,
        value: null,
      });
      setIsDirtySearch(true);
    }
  }, [isDirtySearch, defaultSearchLocationName, selectedAddress]);

  const handleChange = useCallback((value) => {
    if (!value) {
      setAutocompleteValue(null);
    }
    const payload = {
      tripId,
      selectedAddress: value,
      searchObj: {
        page: 1,
      },
    };
    if (isHotelTripSearch) {
      dispatch(setHotelTripSearchInputs({ selectedAddress: value, page: 1 }));
    } else {
      dispatch(hotelSearch(payload));
    }
    track('hotelsSearchLocation.handleChange', {
      selectedAddress: !value ? null : { label: value.label, value: value.value },
    });
  }, [dispatch, isHotelTripSearch, tripId, track]);
  const { doPrioritizeHotelsOnAutoCompleteResults } = travelAgency?.settings || {};
  const autoCompleteTypes = getAutocompleteType({ doPrioritizeHotelsOnAutoCompleteResults });
  const handleInputChange = useCallback((query) => {
    dispatch(hotelAutocompleteRequest({
      autocompleteTypes: autoCompleteTypes.HOTELS,
      responseType: 'HotelsAutocompleteResults',
      query,
    }));
    setIsUserSearch(true);
  }, [autoCompleteTypes.HOTELS, dispatch]);

  const handleBlur = useCallback(() => {
    if (!selectedAddress) {
      setIsDirtySearch(false);
    }
    dispatch(clearAutocomplete({
      responseType: 'HotelsAutocompleteResults',
    }));
  }, [selectedAddress, dispatch]);

  // const handleFocus = useCallback(() => {
  //   dispatch(hotelAutocompleteRequest({
  //     autocompleteTypes: AutocompleteType.HOTELS,
  //     responseType: 'HotelsAutocompleteResults',
  //     query: autocompleteValue && autocompleteValue.label,
  //   }));
  // }, [dispatch, autocompleteValue]);

  return (
    <div className={clsx({ 'w-56': !isHotelTripSearch, 'w-full': isHotelTripSearch })}>
      <SearchAutocomplete
        placeholderText={hotelAddressInputPlaceholder || 'Enter Address'}
        onChange={(newValue) => handleChange(newValue)}
        options={hotelsAutocompleteResults}
        onInputChange={handleInputChange}
        values={autocompleteValue}
        popperMinWidth={350}
        onBlur={handleBlur}
        // onFocus={handleFocus}
        variant={isHotelTripSearch ? [SearchAutocomplete.Variants.outlineNoRightRadius,
          SearchAutocomplete.Variants.outlineNoBorderWidth] : undefined}
        isLoading={hotelAutocompleteRequestStatus === SearchStatus.SEARCHING}
        disableClearable
        doUseThemeBackground={isHotelTripSearch ? doUseThemeBackground : undefined}
      />
    </div>
  );
};

HotelsSearch.propTypes = {
  tripId: PropTypes.string,
  defaultSearchLocationName: PropTypes.string,
  doUseThemeBackground: PropTypes.bool,
};

HotelsSearch.defaultProps = {
  defaultSearchLocationName: null,
  tripId: undefined,
  doUseThemeBackground: false,
};

export default memo(HotelsSearch);
